import React from 'react';
import FormattedNumber from 'components/FormattedNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { InfoRow, usePlanTabContentStyle } from './PlanDetailPanel.helpers';

export interface PlanProjectedRevenueTabProps {
  estimatedAreaUnit?: string;
  yieldQuantityTotal: number;
  yieldPerAcre: number;
  pricePerUnit: number;
  yieldRevenueTotal: number;
  yieldRevenuePerAcre: number;
  fsaPaymentTotal: number;
  fsaPaymentPerAcre: number;
  cropSharePercentValue: number;
  displayByFinancialAccess: boolean;
}

/**
 * The Plans detail panel's "Projected Revenue" tab: Yield Total (a raw
 * quantity, price-per-unit folded into its sub-line) → Yield Revenue → FSA
 * Payment → Crop Share (a percentage, default 100%, per design feedback
 * correcting an earlier dollar-amount treatment).
 */
const PlanProjectedRevenueTab: React.FC<PlanProjectedRevenueTabProps> = ({
  estimatedAreaUnit,
  yieldQuantityTotal,
  yieldPerAcre,
  pricePerUnit,
  yieldRevenueTotal,
  yieldRevenuePerAcre,
  fsaPaymentTotal,
  fsaPaymentPerAcre,
  cropSharePercentValue,
  displayByFinancialAccess
}) => {
  const tabContentStyle = usePlanTabContentStyle();

  return (
    <div className="details-tab-container" style={tabContentStyle}>
      <div
        className="px-6 py-8 flex flex-col gap-4 min-w-0"
        data-testid="plan-detail-tab-panel-projected-revenue"
        style={{ paddingBottom: 72 }}
      >
        <InfoRow label="Yield Total">
          {displayByFinancialAccess && (
            <>
              <div>
                <FormattedNumber roundedTo={2}>
                  {yieldQuantityTotal}
                </FormattedNumber>{' '}
                units
              </div>
              <div className="text-xs text-neutral-60">
                <FormattedNumber roundedTo={2}>{yieldPerAcre}</FormattedNumber>{' '}
                units/{getAbbreviatedUnit(estimatedAreaUnit)} at{' '}
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {pricePerUnit}
                </FormattedNumber>
                /unit
              </div>
            </>
          )}
        </InfoRow>
        <InfoRow label="Yield Revenue">
          {displayByFinancialAccess && (
            <>
              <div>
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {yieldRevenueTotal}
                </FormattedNumber>
              </div>
              <div className="text-xs text-neutral-60">
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {yieldRevenuePerAcre}
                </FormattedNumber>
                /{getAbbreviatedUnit(estimatedAreaUnit)}
              </div>
            </>
          )}
        </InfoRow>
        <InfoRow label="FSA Payment">
          {displayByFinancialAccess && (
            <>
              <div>
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {fsaPaymentTotal}
                </FormattedNumber>
              </div>
              <div className="text-xs text-neutral-60">
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {fsaPaymentPerAcre}
                </FormattedNumber>
                /{getAbbreviatedUnit(estimatedAreaUnit)}
              </div>
            </>
          )}
        </InfoRow>
        <InfoRow label="Crop Share">
          {displayByFinancialAccess && `${cropSharePercentValue.toFixed(2)}%`}
        </InfoRow>
      </div>
    </div>
  );
};

export default PlanProjectedRevenueTab;
